import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const s = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── Section 1: Legal Structures ──
function LegalStructures() {
  const [active, setActive] = useState(0);
  const structures = [
    {
      icon: "🏛️", label: "Trust", fullName: "Mutual Fund Trust (Open-End Trust)", color: gold,
      badge: "Most Common in Canada",
      tagline: "Investors are Unitholders. Open-ended — unlimited units.",
      rows: [
        ["Legal Form", "Unincorporated trust"],
        ["Investors Called", "Unitholders"],
        ["Unit Limit", "Open-ended — no limit. New units created on purchase; cancelled on redemption"],
        ["Taxation", "Flow-through entity — if all income is distributed, the trust pays no tax. Unitholders pay at their individual rate"],
        ["Prevalence", "The vast majority of Canadian mutual funds use this structure"],
      ],
      pills: ["Open-Ended", "Flow-Through Tax", "No Double Taxation", "Unitholders"],
    },
    {
      icon: "🏢", label: "Corporation", fullName: "Mutual Fund Corporation", color: blue2,
      badge: "Switch Fund Advantage",
      tagline: "Investors are Shareholders. Multiple classes under one umbrella.",
      rows: [
        ["Legal Form", "A corporation whose purpose is to invest money"],
        ["Investors Called", "Shareholders"],
        ["Key Advantage", "Switch Funds — multiple share classes (e.g., Global Equity Class, Bond Class) under one corporate entity"],
        ["Tax on Switches", "Historically: switching between classes did NOT trigger a capital gain. 2016 tax changes restricted this benefit, but corporate class funds still offer distribution tax advantages"],
        ["Use Case", "Investors who want to rebalance between asset classes with reduced immediate tax friction"],
      ],
      pills: ["Switch Funds", "Multiple Classes", "Shareholders", "Tax Efficiency"],
    },
  ];
  const st = structures[active];
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2 }}>
        {structures.map((s, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 20px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? s.color + "44" : border}`, borderBottom: active === i ? `2px solid ${s.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? s.color : muted, transition: "all 0.2s" }}>
            {s.icon} {s.label}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${st.color}22`, borderRadius: "0 0 8px 8px", padding: 26 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.15rem", color: white }}>{st.fullName}</div>
          <span style={{ fontSize: "0.62rem", fontWeight: 700, padding: "2px 10px", borderRadius: 100, color: st.color, background: `${st.color}18`, border: `1px solid ${st.color}33`, letterSpacing: "0.1em", textTransform: "uppercase" }}>{st.badge}</span>
        </div>
        <div style={{ fontSize: "0.82rem", color: muted, fontStyle: "italic", marginBottom: 18 }}>{st.tagline}</div>
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          {st.rows.map(([lbl, val], i) => (
            <div key={i} style={{ background: navy3, borderRadius: 6, padding: "11px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: st.color, width: 120, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.85rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
        </div>
        <div>{st.pills.map((p, i) => <Pill key={i} text={p} color={st.color} />)}</div>
      </div>
    </div>
  );
}

// ── Section 2: Key Participants ──
function ParticipantsWheel() {
  const [active, setActive] = useState(null);
  const roles = [
    { icon: "🏗️", name: "Fund Manager", color: gold, short: "The Architect", desc: "Establishes the fund, hires the portfolio manager, calculates NAV daily, prepares Fund Facts documents, and handles marketing and compliance.", analogy: "Think of the Fund Manager as the CEO — they build and run the overall fund business." },
    { icon: "📈", name: "Portfolio Manager", color: blue2, short: "The Investor", desc: "Makes all day-to-day buy and sell decisions on the underlying securities (stocks, bonds, etc.) to meet the fund's stated investment objective.", analogy: "The Portfolio Manager is the one with their hands on the wheel — actually investing the money." },
    { icon: "🏦", name: "Custodian", color: green, short: "The Vault", desc: "Usually a major bank or trust company. Physically holds all of the fund's cash and securities. Separated from the Fund Manager to prevent theft or fraud.", analogy: "Like a bank vault — the custodian keeps the assets safe from anyone who might misuse them." },
    { icon: "📋", name: "Registrar", color: purple, short: "The Record Keeper", desc: "Maintains the official list of all unitholders and processes all purchases and redemptions. Ensures accurate records of who owns what.", analogy: "The Registrar is like the HR database — keeps track of every 'employee' (unitholder) in the fund." },
    { icon: "🤝", name: "Distributor", color: "#f97316", short: "The Seller", desc: "The mutual fund dealer or financial advisor who sells fund units to investors. Earns a trailing commission (included in MER) for ongoing service.", analogy: "The Distributor is the sales team — the bridge between the fund company and the end investor." },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 14 }}>
        {roles.map((role, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? role.color + "55" : border}`, borderRadius: 8, padding: "18px 14px", cursor: "pointer", transition: "all 0.2s", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: role.color }} />
            <div style={{ fontSize: "1.6rem", marginBottom: 8 }}>{role.icon}</div>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: white, marginBottom: 2 }}>{role.name}</div>
            <div style={{ fontSize: "0.72rem", color: role.color }}>{role.short}</div>
          </div>
        ))}
      </div>
      {active !== null && (
        <div style={{ background: navy4, border: `1px solid ${roles[active].color}44`, borderRadius: 8, padding: 22, transition: "all 0.3s" }}>
          <div style={{ display: "flex", align: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: "1.4rem" }}>{roles[active].icon}</span>
            <div>
              <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white }}>{roles[active].name}</div>
              <div style={{ fontSize: "0.75rem", color: roles[active].color }}>{roles[active].short}</div>
            </div>
          </div>
          <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75, marginBottom: 12 }}>{roles[active].desc}</div>
          <div style={{ background: navy3, borderLeft: `3px solid ${roles[active].color}`, borderRadius: 6, padding: "10px 14px", fontSize: "0.82rem", color: muted, fontStyle: "italic" }}>💡 {roles[active].analogy}</div>
        </div>
      )}
      <Callout icon="🔒" accent={green}>
        <strong>Why Separation Matters:</strong> The Fund Manager, Custodian, and Registrar are intentionally <strong>separate entities</strong>. This creates checks and balances — the person managing investments cannot also access the assets or control the records. This protects investors from fraud or misappropriation.
      </Callout>
    </div>
  );
}

// ── Section 3: Forward Pricing + NAVPU ──
function ForwardPricingViz() {
  const [orderTime, setOrderTime] = useState(14); // hour of day (0-23)
  const cutoff = 16; // 4pm
  const isBefore = orderTime < cutoff;
  const pricingDay = isBefore ? "Same Business Day" : "Next Business Day";
  const pricingColor = isBefore ? green : gold;

  const hours = Array.from({ length: 18 }, (_, i) => i + 6); // 6am - 11pm

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🕓 Forward Pricing Simulator</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Drag the order time to see which day's closing NAVPU the investor will receive. Market cutoff is <strong style={{ color: gold }}>4:00 PM ET</strong>.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Order Time</label>
        <input type="range" min={6} max={23} step={1} value={orderTime} onChange={e => setOrderTime(parseInt(e.target.value))} style={{ flex: 1, accentColor: gold }} />
        <span style={{ fontFamily: "monospace", fontSize: "0.95rem", color: gold, width: 60, textAlign: "right" }}>
          {orderTime === 0 ? "12:00 AM" : orderTime < 12 ? `${orderTime}:00 AM` : orderTime === 12 ? "12:00 PM" : `${orderTime - 12}:00 PM`}
        </span>
      </div>
      {/* Timeline bar */}
      <div style={{ position: "relative", height: 48, marginBottom: 20 }}>
        <div style={{ position: "absolute", inset: "50% 0", transform: "translateY(-50%)", height: 6, background: navy4, borderRadius: 3 }} />
        <div style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", height: 6, width: `${((cutoff - 6) / 17) * 100}%`, background: green, borderRadius: 3, transition: "width 0.3s" }} />
        <div style={{ position: "absolute", left: `${((cutoff - 6) / 17) * 100}%`, top: "50%", transform: "translateY(-50%)", height: 6, right: 0, background: gold, borderRadius: 3 }} />
        {/* Cutoff marker */}
        <div style={{ position: "absolute", left: `${((cutoff - 6) / 17) * 100}%`, top: 0, bottom: 0, width: 2, background: white, borderRadius: 1 }} />
        <div style={{ position: "absolute", left: `${((cutoff - 6) / 17) * 100}%`, top: -2, fontSize: "0.65rem", color: white, transform: "translateX(-50%)", fontWeight: 700 }}>4 PM cutoff</div>
        {/* Order dot */}
        <div style={{ position: "absolute", left: `${((orderTime - 6) / 17) * 100}%`, top: "50%", transform: "translate(-50%,-50%)", width: 16, height: 16, borderRadius: "50%", background: pricingColor, border: `2px solid ${navy}`, boxShadow: `0 0 8px ${pricingColor}`, transition: "left 0.2s, background 0.3s", zIndex: 2 }} />
      </div>
      <div style={{ background: navy4, border: `1px solid ${pricingColor}44`, borderRadius: 8, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
        <span style={{ fontSize: "1.8rem" }}>{isBefore ? "✅" : "⏭️"}</span>
        <div>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: pricingColor, marginBottom: 4 }}>Order placed {isBefore ? "before" : "after"} 4:00 PM cutoff</div>
          <div style={{ fontSize: "0.88rem", color: white }}>Receives: <strong style={{ color: pricingColor }}>{pricingDay}'s</strong> closing NAVPU</div>
          <div style={{ fontSize: "0.78rem", color: muted, marginTop: 4 }}>The investor does not know the exact price they will pay — this is forward pricing.</div>
        </div>
      </div>
    </div>
  );
}

function NAVPUCalc() {
  const [stocks, setStocks] = useState(50000000);
  const [cash, setCash] = useState(2000000);
  const [liabilities, setLiabilities] = useState(500000);
  const [units, setUnits] = useState(2500000);
  const [result, setResult] = useState(null);

  function calc() {
    const s = parseFloat(stocks) || 0, c = parseFloat(cash) || 0;
    const l = parseFloat(liabilities) || 0, u = parseFloat(units) || 1;
    const nav = (s + c) - l;
    const navpu = nav / u;
    setResult({ nav, navpu: navpu.toFixed(2), s, c, l, u });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 NAVPU Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 20, lineHeight: 1.8 }}>
        NAV = Total Assets − Total Liabilities<br />
        NAVPU = NAV ÷ Units Outstanding
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 16 }}>
        {[["Stock Portfolio Value ($)", stocks, setStocks], ["Cash on Hand ($)", cash, setCash], ["Total Liabilities ($)", liabilities, setLiabilities], ["Units Outstanding", units, setUnits]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.85rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate NAVPU →</button>
      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 20, marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            {[["Total NAV", `$${result.nav.toLocaleString()}`, blue2], ["NAVPU (Unit Price)", `$${result.navpu}`, gold]].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "12px 14px", textAlign: "center" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontFamily: "monospace", fontSize: "1.3rem", fontWeight: 700, color: white }}>{val}</div>
              </div>
            ))}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, borderTop: `1px solid ${border}`, paddingTop: 12 }}>
            {[
              `Step 1 — Total Assets: $${result.s.toLocaleString()} + $${result.c.toLocaleString()} = $${(result.s + result.c).toLocaleString()}`,
              `Step 2 — NAV: $${(result.s + result.c).toLocaleString()} − $${result.l.toLocaleString()} = $${result.nav.toLocaleString()}`,
              `Step 3 — NAVPU: $${result.nav.toLocaleString()} ÷ ${result.u.toLocaleString()} units = $${result.navpu}`,
            ].map((step, i) => (
              <li key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Section 4: MER ──
function MERBreakdown() {
  const [total, setTotal] = useState(1800000);
  const [assets, setAssets] = useState(90000000);
  const [result, setResult] = useState(null);

  function calc() {
    const t = parseFloat(total) || 0, a = parseFloat(assets) || 1;
    const mer = (t / a * 100).toFixed(2);
    setResult({ mer, t, a });
  }

  const components = [
    { icon: "👔", label: "Management Fee", color: gold, desc: "Pays the portfolio manager and fund company's profit margin. Usually the largest component.", included: true },
    { icon: "🤝", label: "Trailing Commission", color: blue2, desc: "Ongoing fee paid to the advisor/dealer for continuing to service the investor's account.", included: true },
    { icon: "⚖️", label: "Operating Expenses", color: purple, desc: "Legal, audit, custodial, and registrar fees — the cost of running the fund infrastructure.", included: true },
    { icon: "🏛️", label: "GST/HST on Fees", color: green, desc: "Taxes on the above services are also included in the MER.", included: true },
    { icon: "🚫", label: "Brokerage Commissions", color: red, desc: "Trading costs when the portfolio manager buys or sells securities. NOT in MER — captured in the Trading Expense Ratio (TER) instead.", included: false },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gap: 8, marginBottom: 24 }}>
        {components.map((comp, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${comp.included ? border : "rgba(239,68,68,.2)"}`, borderLeft: `3px solid ${comp.color}`, borderRadius: 8, padding: "14px 18px", display: "flex", gap: 14, alignItems: "center" }}>
            <span style={{ fontSize: "1.2rem", flexShrink: 0 }}>{comp.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2, flexWrap: "wrap" }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 700, color: white }}>{comp.label}</div>
                <span style={{ fontSize: "0.62rem", fontWeight: 700, padding: "2px 8px", borderRadius: 100, color: comp.included ? green : red, background: comp.included ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)", border: `1px solid ${comp.included ? "rgba(34,197,94,.3)" : "rgba(239,68,68,.3)"}` }}>
                  {comp.included ? "✓ IN MER" : "✗ NOT IN MER"}
                </span>
              </div>
              <div style={{ fontSize: "0.8rem", color: muted, lineHeight: 1.6 }}>{comp.desc}</div>
            </div>
          </div>
        ))}
      </div>
      {/* MER Calculator */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 MER Calculator</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 16, display: "inline-block" }}>
          MER = (Total Annual Expenses ÷ Average Net Assets) × 100
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[["Total Annual Expenses ($)", total, setTotal], ["Average Net Assets ($)", assets, setAssets]].map(([lbl, val, set], i) => (
            <div key={i}>
              <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
              <input type="number" value={val} onChange={e => set(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.85rem", outline: "none" }} />
            </div>
          ))}
        </div>
        <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "11px 24px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate MER →</button>
        {result && (
          <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 18, marginTop: 14 }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 4 }}>MER</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 900, color: white }}>{result.mer}%</div>
            <div style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${border}` }}>
              → ${ result.t.toLocaleString()} ÷ ${result.a.toLocaleString()} × 100 = {result.mer}%<br />
              → On a $10,000 investment, this fund costs ~${(10000 * parseFloat(result.mer) / 100).toFixed(0)} per year in fees.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Section 5: Distribution & Reinvestment ──
function DistributionCalc() {
  const [units, setUnits] = useState(1000);
  const [navpu, setNavpu] = useState(10.00);
  const [distPerUnit, setDistPerUnit] = useState(0.50);
  const [result, setResult] = useState(null);

  function calc() {
    const u = parseFloat(units) || 0;
    const n = parseFloat(navpu) || 0;
    const d = parseFloat(distPerUnit) || 0;
    const totalValue = u * n;
    const totalDist = u * d;
    const newNavpu = n - d;
    const newUnits = totalDist / newNavpu;
    const totalUnits = u + newUnits;
    const finalValue = totalUnits * newNavpu;
    setResult({ u, n, d, totalValue, totalDist, newNavpu: newNavpu.toFixed(2), newUnits: newUnits.toFixed(4), totalUnits: totalUnits.toFixed(4), finalValue: finalValue.toFixed(2) });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🔄 Distribution &amp; Reinvestment Simulator</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        When a fund distributes income, the NAVPU drops by the distribution amount. Reinvestment buys new units at the lower price. Your total wealth stays the same — but you now own more units and have a tax liability.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 16 }}>
        {[["Units Owned", units, setUnits], ["Current NAVPU ($)", navpu, setNavpu], ["Distribution Per Unit ($)", distPerUnit, setDistPerUnit]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="any" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Simulate →</button>

      {result && (
        <div style={{ marginTop: 16 }}>
          {/* Before / After cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            {[
              { label: "Before Distribution", units: result.u, navpu: result.n.toFixed(2), value: result.totalValue.toFixed(2), color: blue2 },
              { label: "After Reinvestment", units: result.totalUnits, navpu: result.newNavpu, value: result.finalValue, color: gold },
            ].map((card, i) => (
              <div key={i} style={{ background: navy4, border: `1px solid ${card.color}33`, borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: card.color, marginBottom: 10 }}>{card.label}</div>
                {[["Units", card.units], ["NAVPU", `$${card.navpu}`], ["Total Value", `$${parseFloat(card.value).toLocaleString()}`]].map(([lbl, val], j) => (
                  <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${border}` }}>
                    <span style={{ fontSize: "0.78rem", color: muted }}>{lbl}</span>
                    <span style={{ fontFamily: "monospace", fontSize: "0.82rem", color: textCol }}>{typeof val === "number" ? val.toLocaleString(undefined, { maximumFractionDigits: 4 }) : val}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* Steps */}
          <div style={{ background: navy4, borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: gold, marginBottom: 8 }}>Step-by-Step Working</div>
            {[
              `Total distribution: ${result.u} units × $${result.d} = $${result.totalDist.toLocaleString()}`,
              `New NAVPU: $${result.n} − $${result.d} = $${result.newNavpu}`,
              `New units purchased: $${result.totalDist.toLocaleString()} ÷ $${result.newNavpu} = ${result.newUnits} units`,
              `Total units: ${result.u} + ${result.newUnits} = ${result.totalUnits}`,
              `Final value: ${result.totalUnits} × $${result.newNavpu} = $${result.finalValue}`,
            ].map((step, i) => (
              <div key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </div>
            ))}
            <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(245,166,35,.07)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 6, fontSize: "0.82rem", color: textCol }}>
              ✓ Total value unchanged — but the investor now owns more units at a lower price and has a <strong style={{ color: gold }}>tax liability</strong> of ${result.totalDist.toLocaleString()} (the distribution is taxable in the year received).
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which entity is responsible for the safekeeping of a mutual fund's assets?", opts: ["The Registrar", "The Custodian", "The Fund Manager", "The Distributor"], correct: 1, fb: { y: "Correct! The Custodian — usually a major bank or trust company — physically holds all cash and securities. They are separate from the Fund Manager to prevent fraud.", n: "Not quite. The Custodian holds the assets. The Registrar keeps records. The Fund Manager runs the fund. The Distributor sells it." } },
  { q: "What is the defining characteristic of an 'Open-End' mutual fund?", opts: ["It trades on the stock exchange like a stock", "It has a fixed number of shares outstanding", "It continuously issues and redeems units on demand", "It is open only to accredited investors"], correct: 2, fb: { y: "Correct! An open-end fund has no limit on units — new units are created when investors buy and cancelled when they redeem. The fund expands and contracts with demand.", n: "Not quite. Open-end funds continuously create and cancel units. Closed-end funds have a fixed number of units and trade on stock exchanges." } },
  { q: "If an investor places a buy order at 2:00 PM on Tuesday, what price will they receive?", opts: ["The opening price on Tuesday", "The price at exactly 2:00 PM Tuesday", "The closing NAVPU on Tuesday", "The closing NAVPU on Wednesday"], correct: 2, fb: { y: "Correct! Forward pricing: orders placed before 4:00 PM get that same day's closing NAVPU. The investor doesn't know the exact price — they receive it after the fact.", n: "Not quite. Forward pricing means a 2 PM order gets Tuesday's closing NAVPU (4 PM calculation). Only orders placed AFTER 4 PM get Wednesday's price." } },
  { q: "Which of the following expenses is NOT typically included in the MER?", opts: ["Audit Fees", "Brokerage / Trading Commissions", "Trailing Commissions", "Management Fees"], correct: 1, fb: { y: "Correct! Brokerage/trading commissions are NOT in the MER — they are captured in the separate Trading Expense Ratio (TER). The MER covers management, trailing commissions, operating costs, and taxes.", n: "Not quite. Brokerage commissions (trading costs) are excluded from the MER — they appear in the TER. Everything else (audit, trailing, management fees) IS in the MER." } },
  { q: "Calculate NAVPU: Assets = $10M, Liabilities = $1M, Units = 900,000", opts: ["$11.00", "$9.00", "$10.00", "$10.10"], correct: 2, fb: { y: "Correct! NAV = $10M − $1M = $9M. NAVPU = $9,000,000 ÷ 900,000 = $10.00 per unit.", n: "Not quite. NAV = Assets − Liabilities = $10M − $1M = $9M. NAVPU = $9M ÷ 900,000 = $10.00." } },
  { q: "When a mutual fund pays a distribution to unitholders, what happens to the NAVPU?", opts: ["It rises by the amount of the distribution", "It falls by the amount of the distribution", "It remains completely unchanged", "It splits 2-for-1"], correct: 1, fb: { y: "Correct! When the fund pays out cash, the value of the pool decreases by that amount. NAVPU drops by exactly the per-unit distribution paid.", n: "Not quite. NAVPU falls by the distribution amount — the cash has left the fund. If $0.50/unit is paid, a $10.00 NAVPU becomes $9.50." } },
  { q: "What is the primary role of the Registrar?", opts: ["To pick stocks and make investment decisions", "To hold the fund's cash and securities", "To maintain unitholder records and process transactions", "To audit the fund's financial statements"], correct: 2, fb: { y: "Correct! The Registrar is the record keeper — maintaining the official list of unitholders and processing all purchases and redemptions.", n: "Not quite. The Registrar keeps records. The Portfolio Manager picks stocks. The Custodian holds assets. An external auditor audits the fund." } },
  { q: "Which structure allows for a 'Switch Fund' where investors can move between asset classes without immediate tax consequences?", opts: ["Mutual Fund Trust", "Mutual Fund Corporation", "Exchange Traded Fund", "Limited Partnership"], correct: 1, fb: { y: "Correct! Mutual Fund Corporations can have multiple share classes. Historically, switching between classes didn't trigger a capital gain. 2016 tax changes restricted this, but corporate class funds retain some tax advantages.", n: "Not quite. Switch Funds are a feature of the Mutual Fund Corporation structure — multiple classes within one corporate entity allow for tax-efficient rebalancing." } },
  { q: "A mutual fund is described as a 'Flow-Through' entity. What does this mean?", opts: ["Money flows freely between the custodian and manager", "The fund pays all taxes before distributing earnings", "The fund passes its income and tax liability to unitholders to avoid double taxation", "The fund flows through the banking payment system"], correct: 2, fb: { y: "Correct! A flow-through (conduit) entity passes income and its associated tax liability directly to investors. The trust itself pays no tax, so income is only taxed once — at the unitholder level.", n: "Not quite. Flow-through means the fund doesn't pay tax — it passes earnings AND the tax liability to unitholders. This avoids double taxation." } },
  { q: "The majority of mutual funds in Canada are structured as:", opts: ["Closed-End Funds", "Mutual Fund Corporations", "Mutual Fund Trusts", "Limited Partnerships"], correct: 2, fb: { y: "Correct! The vast majority of Canadian mutual funds are structured as Mutual Fund Trusts — open-end trusts where investors are called unitholders.", n: "Not quite. Mutual Fund Trusts (open-end trusts) dominate the Canadian mutual fund industry. Corporations exist but are less common." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 10 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered mutual fund structure and operations." : score >= 8 ? "Excellent — review any missed questions." : score >= 6 ? "Good start. Revisit the key participants and NAVPU mechanics." : "Focus on the five participants, forward pricing, and NAVPU calculation."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Mutual Fund", def: "A pool of money managed by a professional portfolio manager to achieve specific investment objectives." },
  { term: "Mutual Fund Trust", def: "Most common Canadian structure. Open-ended, investors are unitholders, flow-through taxation — the trust pays no tax if all income is distributed." },
  { term: "Mutual Fund Corporation", def: "Corporate structure with multiple share classes. Allows for Switch Funds — tax-efficient movement between asset classes." },
  { term: "Open-End Fund", def: "No limit on units. New units created on purchase; existing units cancelled on redemption. Price set daily at NAVPU." },
  { term: "Unitholder", def: "An investor in a mutual fund trust. Owns units of the fund rather than shares." },
  { term: "Switch Fund", def: "A Mutual Fund Corporation feature — investors can move between different share classes without triggering an immediate capital gain." },
  { term: "Flow-Through Entity", def: "The trust passes income and tax liability directly to unitholders, avoiding double taxation. The trust itself pays no tax." },
  { term: "Fund Manager", def: "Establishes the fund, hires the portfolio manager, calculates NAV daily, prepares Fund Facts, and oversees marketing." },
  { term: "Portfolio Manager", def: "Makes the day-to-day buy and sell decisions on the fund's underlying securities to meet the stated objective." },
  { term: "Custodian", def: "A major bank or trust company that physically holds all fund assets — separated from the manager to prevent fraud." },
  { term: "Registrar", def: "Maintains the list of all unitholders and processes all purchases and redemptions." },
  { term: "Distributor", def: "The mutual fund dealer or advisor who sells fund units to investors. Receives a trailing commission." },
  { term: "Forward Pricing", def: "The rule that investors don't know the exact price when placing an order. Orders before 4 PM receive that day's closing NAVPU." },
  { term: "NAVPU", def: "Net Asset Value Per Unit — the price of one fund unit. Calculated as (Total Assets − Liabilities) ÷ Units Outstanding." },
  { term: "Management Expense Ratio (MER)", def: "Total annual fund costs as a % of average assets. Includes management fee, trailing commission, operating expenses, and taxes." },
  { term: "Trailing Commission", def: "Ongoing fee within the MER paid to the advisor for servicing the client's account." },
  { term: "Trading Expense Ratio (TER)", def: "Captures brokerage commissions — the trading costs NOT included in the MER." },
  { term: "Distribution", def: "When a fund pays out earned income (interest, dividends) or capital gains to unitholders. NAVPU drops by the distribution amount." },
  { term: "Reinvestment", def: "Automatically using distributions to buy new units at the post-distribution (lower) NAVPU. Total value unchanged; more units held." },
];

// ── Main Export ──
export default function Chapter10() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Mutual Fund Legal Structures — Trust vs. Corporation"],
    ["02", "Key Participants & Separation of Functions"],
    ["03", "Forward Pricing & NAVPU Calculation"],
    ["04", "Management Expense Ratio (MER)"],
    ["05", "Distributions & Reinvestment"],
    ["🧮", "NAVPU, MER & Distribution Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        {/* HERO */}
        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 10
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Mutual Fund <span style={{ color: gold }}>Structure &amp; Operations</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              How mutual funds are legally structured, who runs them and why functions are separated, how NAVPU and forward pricing work, the MER and what it includes, and the mechanics of distributions and reinvestment.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","5","Core Topics"],["⏱","~18 min","read"],["🎯","10","practice questions"],["🧮","3","calculators"],["👥","5","key participants"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* TOC */}
        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* ── S1 ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 01</span><span style={s.badge}>Legal Structures</span><div style={s.line} /></div>
            <h2 style={s.h2}>Mutual Fund Legal Structures</h2>
            <p style={s.prose}>
              In Canada, mutual funds can be set up under two legal structures — each with different ownership, tax treatment, and key advantages. The structure determines whether investors are called <span style={{ color: gold, fontWeight: 600 }}>Unitholders</span> (Trust) or <span style={{ color: blue2, fontWeight: 600 }}>Shareholders</span> (Corporation).
            </p>
          </Reveal>
          <Reveal delay={0.1}><LegalStructures /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="💡">
              <strong>The Flow-Through Advantage:</strong> A mutual fund trust is a conduit — it passes income straight to unitholders without paying tax at the fund level. This avoids <strong>double taxation</strong> (once at the fund, again at the investor). As long as the fund distributes all net income, the trust's tax bill is zero.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S2 ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 02</span><span style={s.badge}>Key Participants</span><div style={s.line} /></div>
            <h2 style={s.h2}>Who Runs a Mutual Fund?</h2>
            <p style={s.prose}>
              Five distinct roles are involved in operating a fund. Their functions are <span style={{ color: gold, fontWeight: 600 }}>intentionally separated</span> — the entity managing investments cannot also hold the assets or control the records. Click each participant to learn their role and see an analogy:
            </p>
          </Reveal>
          <Reveal delay={0.1}><ParticipantsWheel /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S3 ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 03</span><span style={s.badge}>Pricing</span><div style={s.line} /></div>
            <h2 style={s.h2}>Forward Pricing &amp; NAVPU</h2>
            <p style={s.prose}>
              Unlike stocks that trade every second, mutual funds are priced <span style={{ color: gold, fontWeight: 600 }}>once per day</span> — typically at 4:00 PM ET. Under forward pricing, investors never know the exact price they'll receive when placing an order. The NAVPU formula is applied to the entire fund pool each day.
            </p>
          </Reveal>
          <Reveal delay={0.1}><ForwardPricingViz /></Reveal>
          <Reveal delay={0.2}><NAVPUCalc /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S4 ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 04</span><span style={s.badge}>MER</span><div style={s.line} /></div>
            <h2 style={s.h2}>Management Expense Ratio (MER)</h2>
            <p style={s.prose}>
              The MER is the total annual cost of running the fund, expressed as a percentage of average assets. It is deducted <span style={{ color: gold, fontWeight: 600 }}>before</span> returns are shown to the investor — so the return you see is already net of MER. Knowing exactly what is and isn't included is a favourite exam topic:
            </p>
          </Reveal>
          <Reveal delay={0.1}><MERBreakdown /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="⚠️" accent={red}>
              <strong style={{ color: red }}>Brokerage Commissions Are NOT in the MER.</strong> Trading costs when the portfolio manager buys or sells securities are captured in the <strong>Trading Expense Ratio (TER)</strong> — a separate figure. This is a common exam trap.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── S5 ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Section 05</span><span style={s.badge}>Distributions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Distributions &amp; Reinvestment</h2>
            <p style={s.prose}>
              At least once a year, mutual funds distribute earned income (interest, dividends) and realized capital gains to unitholders. When the distribution is paid out, the <span style={{ color: gold, fontWeight: 600 }}>NAVPU drops by exactly the distribution amount</span>. Reinvestment immediately buys new units at the lower price — leaving total wealth unchanged but creating a tax event:
            </p>
          </Reveal>
          <Reveal delay={0.1}><DistributionCalc /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="🔑" accent={purple}>
              <strong style={{ color: purple }}>Key Insight on Distributions:</strong> Your total <strong>wealth does not change</strong> on distribution day — you simply have more units at a lower price. But you <strong>do</strong> have a tax liability for the distribution received, even if it was reinvested. This is why December distributions can surprise investors at tax time.
            </Callout>
          </Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Quiz ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Knowledge Check</span><span style={s.badge}>10 Questions</span><div style={s.line} /></div>
            <h2 style={s.h2}>Test Your Understanding</h2>
            <p style={s.prose}>10 questions covering fund structures, participants, NAVPU, MER, forward pricing, and distributions from Chapter 10.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={s.hr} />

        {/* ── Glossary ── */}
        <div style={s.section}>
          <Reveal>
            <div style={s.eyebrow}><span style={s.tag}>Quick Reference</span><span style={s.badge}>Key Terms</span><div style={s.line} /></div>
            <h2 style={s.h2}>Glossary</h2>
            <p style={s.prose}>All essential terms from Chapter 10 — mutual fund structure, participants, pricing, and distributions.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
